import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { median } from 'mathjs';
import { Extent } from 'ol/extent';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';
import { SidebarService } from 'src/app/modules/map/components/sidebar/sidebar.service';
import { SystemFacade } from 'src/app/shared/system/system.facade';
import { WKTtoFeature } from '../../../modules/map/utils/map.utils';
import { LayersService } from '../../services/layers/layers.service';
import { ProjectsService } from '../../services/projects/projects.service';
import { Camada, LayerRepresentation } from '../../types/camada.types';
import { PVG_PORTAL_KEY } from '../../types/projeto.types';
import * as AuthActions from '../auth/auth.actions';
import { AuthFacade } from '../auth/auth.facade';
import * as ProjectsActions from './projects.actions';
import { ProjectsFacade } from './projects.facade';
import { MapLayerWithZoom } from './projects.reducer';
import { MapLayer } from './projects.types';

@Injectable()
export class ProjectsEffects {
  getProjectsIdsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.getProjectsIdsSuccess),
      withLatestFrom(
        this.projectsFacade.icadGeomsQty$,
        this.projectsFacade.pvgeoGeomsQty$,
        this.projectsFacade.mode$
      ),
      switchMap(([_, icadGeomsQty, pvgeoGeomsQty, mode]) => {
        const batchOfActions: any[] = [
          AuthActions.getUserPermission(),
          ProjectsActions.getProjectExtent(),
          // ProjectsActions.getProjectLegend(),
        ];

        if (mode === PVG_PORTAL_KEY) {
          batchOfActions.push(ProjectsActions.clearICADProjectId());
        } else if (mode === 'icad') {
          batchOfActions.push(ProjectsActions.clearOuvProjectsIds());
        } else if (mode === 'simples') {
          batchOfActions.push(ProjectsActions.clearOuvProjectsIds());
          batchOfActions.push(ProjectsActions.clearICADProjectId());
        }

        const icadQtyIsNumber = typeof icadGeomsQty === 'number';
        const pvgeoQtyIsNumber = typeof pvgeoGeomsQty === 'number';
        const noGeoms =
          icadQtyIsNumber &&
          pvgeoQtyIsNumber &&
          icadGeomsQty === 0 &&
          pvgeoGeomsQty === 0;

        if (noGeoms) {
          this.sidebarService.setFirstSidebarVisible(false);
          this.sidebarService.setSecondSidebarVisible(false);
        }

        return batchOfActions;
      })
    )
  );

  getProjectExtent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.getProjectExtent),
      withLatestFrom(
        this.projectsFacade.pvgeoId$,
        this.projectsFacade.pvgeoGeomsQty$,
        this.projectsFacade.icadId$,
        this.projectsFacade.icadGeomsQty$,
        this.projectsFacade.zoneId$,
        this.projectsFacade.mode$
      ),
      switchMap(
        ([_, pvgeoId, pvgeoGeomsQty, icadId, icadGeomsQty, zoneId, mode]) => {
          let id = pvgeoId || icadId || zoneId;

        const icadQtyIsNumber = typeof icadGeomsQty === 'number';
        const pvgeoQtyIsNumber = typeof pvgeoGeomsQty === 'number';
        const noGeoms =
          icadQtyIsNumber &&
          pvgeoQtyIsNumber &&
          icadGeomsQty === 0 &&
          pvgeoGeomsQty === 0;

        if (noGeoms) {
          id = zoneId;
        }
          if (!id || id === -1) {
            return EMPTY;
          }

          if (mode === 'icad') {
            id = icadId;
          }

          if (id === null || id === undefined) {
            id = icadId || pvgeoId || zoneId;
          }

          return this.layersService.getAllLayersExtent(id);
        }
      ),
      map((extentWKT: string) => {
        if (!extentWKT) {
          return '';
        }
        return WKTtoFeature(extentWKT).getGeometry()?.getExtent();
      }),
      map((extent: Extent) =>
        ProjectsActions.getProjectExtentSuccess({ extent })
      )
    )
  );

  getProjectLegend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.getProjectLegend),
      withLatestFrom(
        this.projectsFacade.pvgeoId$,
        this.projectsFacade.ouvCollabId$,
        this.projectsFacade.icadId$
      ),
      switchMap(([_, pvgeoId, ouvCollabId]) => {
        if (pvgeoId || ouvCollabId) {
          return this.projectsService.getLegend().pipe(
            map((legend: any) =>
              ProjectsActions.getProjectLegendSuccess({ legend })
            ),
            catchError((error) =>
              of(ProjectsActions.getProjectLegendFail({ error }))
            )
          );
        }

        return EMPTY;
      })
    )
  );

  // Um pedido por vez. Se a camada não existir no projeto (ex.: "Avaliações Salvas"),
  // o pedido nunca termina e trava todos os formulários pedidos depois dele.
  getLayerForms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.getLayerForms),
      withLatestFrom(this.systemFacade.isCriciuma$),
      filter(([, isCriciuma]) => !isCriciuma),
      concatMap(([{ layerName }]) =>
        this.loadLayerForms(
          this.projectsFacade
            .selectMapLayerByName$(layerName)
            .pipe(filter((id) => !!id))
        )
      )
    )
  );

  // Criciúma: camadas diferentes carregam em paralelo, a mesma camada uma vez só (o facade
  // repete o pedido enquanto os formulários não chegam), e uma camada que não aparece no
  // projeto em 30 s é abandonada sem prender as outras.
  private layerFormsLoading = new Set<string>();

  getLayerFormsCriciuma$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.getLayerForms),
      withLatestFrom(this.systemFacade.isCriciuma$),
      filter(
        ([{ layerName }, isCriciuma]) =>
          isCriciuma && !this.layerFormsLoading.has(layerName)
      ),
      mergeMap(([{ layerName }]) => {
        this.layerFormsLoading.add(layerName);
        return this.loadLayerForms(
          this.projectsFacade.selectMapLayerByName$(layerName).pipe(
            filter((id) => !!id),
            timeout({ first: 30_000, with: () => EMPTY })
          )
        ).pipe(finalize(() => this.layerFormsLoading.delete(layerName)));
      })
    )
  );

  private loadLayerForms(layer$: Observable<MapLayerWithZoom | undefined>) {
    return layer$.pipe(
      switchMap((layer: MapLayerWithZoom) => {
        return this.layersService.getLayerByIdWithForms(layer.id).pipe(
          map(({ projetosCamadasFormularios: layerForms }: Camada) =>
            ProjectsActions.getLayerFormsSuccess({
              layerName: layer.name,
              layerForms,
            })
          ),
          catchError((error) =>
            of(
              ProjectsActions.getLayerFormsFail({
                layerName: layer.name,
                error,
              })
            )
          )
        );
      }),
      take(1)
    );
  }

  getVm2CoreMedian$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.getVM2CoreMedian),
      switchMap(() => this.projectsFacade.mapLayers$),
      map((layers) =>
        layers
          .filter((l) => l.showOuv && l.publicoOuv)
          .find((l) => l.type === LayerRepresentation.PVG)
      ),
      switchMap((pvgLayer: MapLayer) =>
        this.layersService.getAllFeaturesFromLayer(pvgLayer?.id)
      ),
      map((features: any[]) => {
        const vm2CorFieldModel = features[0].valores.find(
          (v) =>
            v.projetoCamadaFormularioCampo.representacao === 'VM2COR' ||
            v.projetoCamadaFormularioCampo.representacao === 'VM2'
        );
        const id = vm2CorFieldModel.projetoCamadaFormularioCampo['@id'];
        return features.reduce((acc1, curr1) => {
          const vm2CorField = curr1.valores.find((v) => {
            const fieldBack = v.projetoCamadaFormularioCampo;
            if (typeof fieldBack === 'string' && fieldBack === id) {
              return v;
            }
            if (typeof fieldBack === 'object' && fieldBack['@id'] === id) {
              return v;
            }
            return false;
          });

          const roundedValue =
            Math.round((vm2CorField.valorNum + Number.EPSILON) * 100) / 100;

          acc1 = [...acc1, roundedValue];
          return acc1;
        }, []);
      }),
      map((values) => values.filter((v) => !isNaN(v))),
      map((values) => median(values)),
      map((vm2CoreMedian) =>
        ProjectsActions.getVM2CoreMedianSuccess({
          vm2CoreMedian: vm2CoreMedian ? vm2CoreMedian : 0,
        })
      )
    )
  );

  setPortalMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.setPortalMode),
      map(({ mode }) => {
        if (mode === PVG_PORTAL_KEY) {
          return ProjectsActions.clearICADProjectId();
        } else {
          return ProjectsActions.clearOuvProjectsIds();
        }
      })
    )
  );

  portalModeClears$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectsActions.clearICADProjectId,
          ProjectsActions.clearOuvProjectsIds
        ),
        withLatestFrom(
          this.projectsFacade.mapLayers$,
          this.projectsFacade.mode$,
          this.authFacade.user$
        ),
        tap(([_, layers, mode, user]) => {
          if (!layers.length) {
            return;
          }

          const isOuv = mode === PVG_PORTAL_KEY;
          const isIcad = mode === 'icad';

          const onlyPrivate = layers.every((l) => {
            return (
              (isOuv && l.showOuv && !l.publicoOuv) ||
              (isIcad && l.showIcad && !l.publicoIcad)
            );
          });

          if (onlyPrivate && !user) {
            setTimeout(() => {
              this.router.navigate(['/auth/login']);
            }, 500);
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private layersService: LayersService,
    private projectsFacade: ProjectsFacade,
    private projectsService: ProjectsService,
    private sidebarService: SidebarService,
    private authFacade: AuthFacade,
    private router: Router,
    private systemFacade: SystemFacade
  ) {}
}
